import { RouteComponentProps } from "@reach/router";
import React, {
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { OnboardingConsents } from "./Consents";
import { OnboardingKYC } from "./KYC";
import { useFetcher, useResource } from "rest-hooks";
import { ConsentResource, UserResource } from "../../../resources";
import { Header } from "../../sections";
import { MyPagePanel } from "../../common";
import { useOnboardingStyles } from "./styles";
import { Container, Grid, LinearProgress, Typography } from "@material-ui/core";
import { useCommonWidths } from "../../../hooks";
import { OnboardingContactInfo } from "./ContactInfo";
import OnboardingResource, {
	OnboardingStatusResult,
} from "../../../resources/OnboardingResource";
import { useInvalidateOnUnmount } from "../../../utils";

type OnboardingWrapperProps = RouteComponentProps & {
	children: JSX.Element;
};

type OnboardingFormPagesProps = {
	consents: ConsentResource[];
	initialOnboardingState: OnboardingCompletionStatus;
	updateOnboardingStatus: (onboardingComplete: boolean) => void;
};

type OnboardingCompletionStatus = {
	contactInfoIsCompleted: boolean;
	consentsIsCompleted: boolean;
	kycIsCompleted: boolean;
};

export type OnboardingFormStateValues = {
	contactInfo: {
		email: string;
		mobilePhone: string;
	};
	consents: {
		ecommunications: "true" | "false";
		emarketing: "true" | "false";
	};
	kyc: {
		pepCheck: string;
		pepRole: string;
		vehicleUsage: string;
		paymentsOrigin: string;
		paymentsAbroadCheck: string;
		paymentsAbroadCountry: string;
		partialPaymentsAbove100K: string;
	};
};

export type CommonOnboardingProps = {
	readonly onboardingStateHandler: {
		state: OnboardingFormStateValues;
		update: React.Dispatch<React.SetStateAction<OnboardingFormStateValues>>;
	};
	readonly navigateBack?: () => void | undefined;
	readonly completeOnboardingPage: () => void;
	readonly isLastStep: boolean;
};

export const OnboardingWrapper = (props: OnboardingWrapperProps) => {
	const consents = useResource(ConsentResource.listShape(), {});
	const user = useResource(UserResource.detailShape(), {});

	const initialOnboardingCompletionStatus: OnboardingCompletionStatus = {
		contactInfoIsCompleted: false,
		consentsIsCompleted: Boolean(
			consents.find((c) => c.id === "ecommunications")?.consented
		),
		kycIsCompleted: !user.needsKycInput,
	};

	const [needsOnboarding, setNeedsOnboarding] = useState(
		!initialOnboardingCompletionStatus.consentsIsCompleted ||
			!initialOnboardingCompletionStatus.kycIsCompleted
	);

	return needsOnboarding ? (
		<OnboardingFormPages
			consents={consents}
			initialOnboardingState={initialOnboardingCompletionStatus}
			updateOnboardingStatus={setNeedsOnboarding}
		/>
	) : (
		// Main content of Min Side
		props.children
	);
};

const OnboardingFormPages = (props: OnboardingFormPagesProps) => {
	const { consents, initialOnboardingState, updateOnboardingStatus } = props;

	const [onboardingCompletionStatus, setOnboardingCompletionStatus] =
		useState<OnboardingCompletionStatus>(initialOnboardingState);
	const [onboardingStateValues, setOnboardingStateValues] =
		useState<OnboardingFormStateValues>({
			contactInfo: {
				email: "",
				mobilePhone: "",
			},
			consents: {
				ecommunications: consents.find((x) => x.id === "ecommunications")
					?.consented
					? "true"
					: "false",
				emarketing: consents.find((x) => x.id === "emarketing")?.consented
					? "true"
					: "false",
			},
			kyc: {
				partialPaymentsAbove100K: "",
				paymentsAbroadCheck: "",
				paymentsAbroadCountry: "",
				paymentsOrigin: "",
				pepCheck: "",
				pepRole: "",
				vehicleUsage: "",
			},
		});
	const [activePageIndex, setActivePageIndex] = useState(0);
	const [onboardingError, setOnboardingError] = useState("");
	const classes = useOnboardingStyles();

	const performOnboarding = useFetcher(OnboardingResource.createShape());

	// Force cache-reload / refetch of Consents and UserInfo when this component unmounts, so that updated information is reflected inside MinSide
	useInvalidateOnUnmount(ConsentResource.listShape(), {});
	useInvalidateOnUnmount(UserResource.detailShape(), {});

	const submitOnboardingAndSignIn = useCallback(async () => {
		setOnboardingError("");

		const submitConsents = !initialOnboardingState.consentsIsCompleted;
		const submitKyc = !initialOnboardingState.kycIsCompleted;

		await performOnboarding(
			{},
			{
				contactInfo: {
					email: onboardingStateValues.contactInfo.email,
					mobilePhone: onboardingStateValues.contactInfo.mobilePhone,
				},
				consents: submitConsents
					? [
							{
								id: "ecommunications",
								consented: onboardingStateValues.consents.ecommunications,
							},
							{
								id: "emarketing",
								consented: onboardingStateValues.consents.emarketing,
							},
					  ]
					: undefined,
				kyc: submitKyc
					? {
							partialPaymentsOver100K:
								onboardingStateValues.kyc.partialPaymentsAbove100K,
							paymentsFromAbroadCheck:
								onboardingStateValues.kyc.paymentsAbroadCheck,
							paymentsFromAbroadCountry:
								onboardingStateValues.kyc.paymentsAbroadCountry,
							paymentsOrigin: onboardingStateValues.kyc.paymentsOrigin,
							pepCheck: onboardingStateValues.kyc.pepCheck,
							pepRole: onboardingStateValues.kyc.pepRole,
							vehicleUsage: onboardingStateValues.kyc.vehicleUsage,
					  }
					: undefined,
			}
		).then((result: OnboardingStatusResult) => {
			if (!result.success && result.errorMessage)
				setOnboardingError(result.errorMessage);
			else {
				updateOnboardingStatus(false);
			}
		});
	}, [
		initialOnboardingState,
		performOnboarding,
		onboardingStateValues,
		updateOnboardingStatus,
	]);

	// Callbacks
	const navigateNextPage = useCallback(
		() => setActivePageIndex((prev) => prev + 1),
		[]
	);
	const finishOnboardingContactInfo = useCallback(() => {
		setOnboardingCompletionStatus({
			...onboardingCompletionStatus,
			contactInfoIsCompleted: true,
		});
		navigateNextPage();
	}, [onboardingCompletionStatus, navigateNextPage]);
	const finishOnboardingConsent = useCallback(() => {
		setOnboardingCompletionStatus({
			...onboardingCompletionStatus,
			consentsIsCompleted: true,
		});
		if (!initialOnboardingState.kycIsCompleted) {
			navigateNextPage();
		}
	}, [onboardingCompletionStatus, initialOnboardingState, navigateNextPage]);
	const finishOnboardingKYC = useCallback(async () => {
		setOnboardingCompletionStatus({
			...onboardingCompletionStatus,
			kycIsCompleted: true,
		});
	}, [onboardingCompletionStatus]);

	// Finding the correct Onboarding-formpage-component to render (if any):
	const onboardingPages: JSX.Element[] = useMemo(() => {
		const pages = [];

		// <ContactInfo /> should ALWAYS be the first OnboardingComponent if either Consents/KYC is needed
		pages.push(
			<OnboardingContactInfo
				completeOnboardingPage={finishOnboardingContactInfo}
				onboardingStateHandler={{
					state: onboardingStateValues,
					update: setOnboardingStateValues,
				}}
				isLastStep={false}
			/>
		);
		if (!initialOnboardingState.consentsIsCompleted) {
			pages.push(
				<OnboardingConsents
					completeOnboardingPage={finishOnboardingConsent}
					onboardingStateHandler={{
						state: onboardingStateValues,
						update: setOnboardingStateValues,
					}}
					navigateBack={() => setActivePageIndex(0)}
					consents={consents}
					isLastStep={initialOnboardingState.kycIsCompleted}
				/>
			);
		}
		if (!initialOnboardingState.kycIsCompleted) {
			pages.push(
				<OnboardingKYC
					completeOnboardingPage={finishOnboardingKYC}
					navigateBack={() => setActivePageIndex((activeIdx) => activeIdx - 1)}
					onboardingStateHandler={{
						state: onboardingStateValues,
						update: setOnboardingStateValues,
					}}
					isLastStep={true}
				/>
			);
		}
		return pages;

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		// finishOnboardingConsent,
		// finishOnboardingContactInfo,
		// finishOnboardingKYC,
		// //onboardingCompletionStatus.consents, // NO!
		// //onboardingCompletionStatus.kyc, // NO!
		onboardingStateValues,
	]);

	// Run whenever `onboardingCompletionStatus` changes
	useEffect(() => {
		// If there still are some uncompleted onboardingStatuses, return here:
		if (
			Object.values(onboardingCompletionStatus).some(
				(status: Boolean) => status === false
			)
		)
			return;

		// All onboarding pages are complete. Update ContactInfo, and Consents/ KYC data.
		const performOnboardingUpdates = async () => {
			await submitOnboardingAndSignIn();
		};

		// Only perform submit when pressing "next" -button on last OnboardingPage
		if (activePageIndex === onboardingPages.length - 1) {
			performOnboardingUpdates();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activePageIndex,
		onboardingCompletionStatus,
		onboardingPages.length,
		//submitOnboardingAndSignIn,
	]);

	return (
		<Grid
			id="contentAndFooterParent"
			className={classes.contentAndFooterParent}
			container
			direction="column"
			justify="space-between"
		>
			<Grid item id="content">
				<Header isOnboardingHeader={true} />
				<MyPagePanel className={classes.root}>
					<Container maxWidth="md">
						{/* Render one of the onboarding pages
								Provide a suspense in for a nicer waiting-period (if any) when loading the <KYC /> component, where we're awaiting 3 resource-fetchings */}
						<Suspense fallback={<LinearProgress />}>
							{onboardingPages[activePageIndex]}
						</Suspense>
						{onboardingError ? (
							<Typography color="error" style={{ fontWeight: "bold" }}>
								{onboardingError}
							</Typography>
						) : (
							<></>
						)}
					</Container>
				</MyPagePanel>
			</Grid>
			<Grid id="footerWrapper" className={classes.footerWrapper}>
				<ContactInfoFooter />
			</Grid>
		</Grid>
	);
};

export const contactFooterHeightDesktop = 90;
export const contactFooterHeightMobile = 185;

const ContactInfoFooter = () => {
	const isMobile = useCommonWidths().isSmDown;
	const classes = useOnboardingStyles();

	return (
		<Grid
			className={classes.contactWrapper}
			container
			direction={isMobile ? "column" : "row"}
			justify="center"
		>
			<Grid item className={classes.contactData}>
				<Typography>
					<span className={classes.contactTitle}>Telefon: </span>
					<a href="tel:400 02 259">400 02 259</a>
				</Typography>
			</Grid>
			<Grid item className={classes.contactData}>
				<Typography>
					<span className={classes.contactTitle}>E-post: </span>
					<a
						href="mailto:post@financiering.no"
						target="_blank"
						rel="noopener noreferrer"
					>
						post@financiering.no
					</a>
				</Typography>
			</Grid>
			<Grid item className={classes.contactData}>
				<Typography>
					<span className={classes.contactTitle}>Åpningstider: </span>Mandag -
					fredag: 08:00 - 16:00
				</Typography>
			</Grid>
		</Grid>
	);
};
